import React from 'react';
import { format } from 'date-fns';
import { Bell, CheckCircle, Info, AlertTriangle, AlertCircle, Check, Trash2, Undo } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Notification } from '@/types/notification';
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { TableCell, TableRow } from "@/components/ui/table";

// Props for the NotificationItem component
export interface NotificationItemProps extends Omit<Notification, 'createdAt'> {
  createdAt: any; // Keep as any for flexibility
  onMarkAsRead: (id: string) => void;
  onMarkAsUnread?: (id: string) => void;
  onRemove: (id: string) => void;
}

export const NotificationItem: React.FC<NotificationItemProps> = ({
  id,
  title,
  message,
  type,
  isRead,
  createdAt,
  onMarkAsRead,
  onMarkAsUnread,
  onRemove,
}) => {
  // Get notification icon based on type
  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'info':
        return <Info className="h-4 w-4 text-blue-500" />;
      case 'warning':
        return <AlertTriangle className="h-4 w-4 text-amber-500" />;
      case 'error':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      case 'success':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      default:
        return <Bell className="h-4 w-4" />;
    }
  };

  // Format date from Timestamp
  const formatDate = (timestamp: any) => {
    if (!timestamp) return '';
    
    try {
      const date = timestamp.toDate();
      return format(date, 'd MMM');
    } catch (error) {
      return '';
    }
  };

  return (
    <TableRow className={cn("border-b", !isRead && "bg-primary/5")}>
      {/* Status Column */}
      <TableCell className="text-center text-muted-foreground py-3 align-middle">
        {isRead ? (
          <Badge variant="outline" className="text-muted-foreground">Read</Badge>
        ) : (
          <Badge variant="default">New</Badge>
        )}
      </TableCell>
      
      {/* Message Column */}
      <TableCell className="text-muted-foreground py-3">
        <p className="whitespace-normal break-words">{message}</p>
      </TableCell>
      
      {/* Date Column - Hidden on small screens */}
      <TableCell className="hidden sm:table-cell text-muted-foreground py-3 text-center align-middle">
        {formatDate(createdAt)}
      </TableCell>
      
      {/* Actions Column */}
      <TableCell className="text-right py-3 align-middle">
        <div className="flex items-center justify-end gap-2">
          <div className="w-8">
            {!isRead ? (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => onMarkAsRead(id)}
                    >
                      <Check className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="left">
                    <p>Mark as read</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : onMarkAsUnread && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => onMarkAsUnread(id)}
                    >
                      <Undo className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="left">
                    <p>Mark as unread</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
          
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="icon"
                  className="h-8 w-8 text-muted-foreground hover:text-destructive"
                  onClick={() => onRemove(id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="left">
                <p>Delete notification</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </TableCell>
    </TableRow>
  );
};

export default NotificationItem; 